import React, { useCallback, useMemo, useState } from "react";
import {
  useExternalContracts,
  useExternalContractDetail,
} from "../hooks/useExternalContracts";
import { useExternalContractFilters } from "../hooks/useExternalContractFilters";
import {
  Package,
  AlertCircle,
  Download,
  ArrowRight,
  Building,
  Banknote,
  Calendar,
  List as ListIcon,
  LayoutGrid,
} from "@semo/icons";
import {
  Skeleton,
  CardSkeleton,
  ContractCard as SharedContractCard,
  CONTRACT_STATUS_CONFIG,
  type ContractStatus,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@semo/shared-ui";
import { ContractSheetModal } from "@semo/shared-chat";
import { cn } from "@semo/shared-ui";

import {
  contractPdfPath,
  downloadPdf,
  type ContractListResponseDto,
} from "@semo/api-contracts";
import { toast } from "sonner";

// 상태 세그먼트 탭 (참여 프로젝트와 동일 스타일)
const CONTRACT_FILTER_TABS = [
  { key: "all", label: "전체" },
  { key: "COMPLETED", label: "완료" },
  { key: "CANCELED", label: "취소됨" },
] as const;

interface ContractListProps {
  initialSelectedId?: string | null;
  /** DEV 전용: 계약 탭을 샘플 데이터로 미리보기 (/dev/preview?tab=contracts). */
  previewData?: ContractListResponseDto[];
}

export const ContractList: React.FC<ContractListProps> = ({
  initialSelectedId,
  previewData,
}) => {
  const { contracts, isLoading, error, refetch } =
    useExternalContracts(previewData);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    initialSelectedId || null,
  );
  const [view, setView] = useState<"card" | "list">("card");

  // 필터링 로직 (Standardized pattern)
  const { activeFilter, setActiveFilter, filteredContracts, counts } =
    useExternalContractFilters(contracts);

  // Sort filtered contracts by date descending
  const sortedContracts = useMemo(() => {
    return [...filteredContracts].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [filteredContracts]);

  /**
   * 저장된 `pdfUrl` 을 그대로 열면 안 된다.
   * 그 값은 비공개 버킷(`documents`)을 가리키는 **공개 형식** 주소라, 브라우저로 열면
   * 언제나 Supabase 의 `{"error":"Bucket not found"}` JSON 이 새 탭에 뜬다.
   * 계약서는 서버 프록시에서 받아온다. (여기서 pdfUrl 은 "PDF 가 준비됐는지" 판단에만 쓴다.)
   */
  const handleDownloadPdf = useCallback(
    async (contract: { id: string; contractNumber: string }) => {
      try {
        await downloadPdf(
          contractPdfPath(contract.id),
          `전자계약서_${contract.contractNumber}.pdf`,
        );
      } catch {
        toast.error("계약서를 불러오지 못했습니다.");
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <Skeleton className="h-[32px] w-[80px]" />
          <Skeleton className="h-[32px] w-[100px]" />
          <Skeleton className="h-[32px] w-[100px]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          <CardSkeleton count={6} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-slate-600 ">
            계약 리스트를 불러오는 중 오류가 발생했습니다.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-y-auto bg-muted/10 p-4 sm:p-6 md:p-8">
      {/* Header — 참여 프로젝트와 톤 통일 (헤딩 + 설명) */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">
          나의 계약
        </h1>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">
          체결된 계약과 주문 내역을 확인하고 계약서를 관리하세요.
        </p>
      </div>

      {/* 상태 세그먼트 탭 + 리스트/카드 토글 */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
          {CONTRACT_FILTER_TABS.map((tab) => {
            const active = activeFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveFilter(tab.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
                  active
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-2xs font-semibold leading-none",
                    active
                      ? "bg-primary/10 text-primary"
                      : "bg-slate-200/70 text-slate-500",
                  )}
                >
                  {counts[tab.key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* 카드 / 리스트 토글 (아이콘 + 글씨) */}
        <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
          <button
            type="button"
            aria-pressed={view === "card"}
            onClick={() => setView("card")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
              view === "card"
                ? "bg-primary/10 text-primary"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            <LayoutGrid className="h-4 w-4" />
            카드
          </button>
          <button
            type="button"
            aria-pressed={view === "list"}
            onClick={() => setView("list")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
              view === "list"
                ? "bg-primary/10 text-primary"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            <ListIcon className="h-4 w-4" />
            리스트
          </button>
        </div>
      </div>

      {sortedContracts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white/50  rounded-2xl border border-dashed border-slate-200 ">
          <Package className="w-16 h-16 text-slate-300  mb-4" />
          <p className="text-slate-500  text-lg">
            {activeFilter === "all"
              ? "등록된 계약이 없습니다."
              : "해당 상태의 계약이 없습니다."}
          </p>
        </div>
      ) : view === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6 pb-8">
          {sortedContracts.map((contract) => (
            <SharedContractCard
              key={contract.id}
              contractNumber={contract.contractNumber}
              projectName={contract.quoteRequest?.projectName || "프로젝트명 없음"}
              status={contract.status as ContractStatus}
              totalAmount={contract.totalAmount}
              createdAt={contract.createdAt}
              counterpartyName={contract.buyer?.name || "정보 없음"}
              counterpartyLabel="발주자"
              pdfUrl={contract.pdfUrl ?? undefined}
              onDownloadPdf={
                contract.pdfUrl
                  ? () => void handleDownloadPdf(contract)
                  : undefined
              }
              onClick={() => setSelectedContractId(contract.id)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4 pb-8">
          {sortedContracts.map((contract) => {
            const sc = CONTRACT_STATUS_CONFIG[contract.status as ContractStatus];
            const SIcon = sc.icon;
            const hasPdf = !!contract.pdfUrl;
            return (
              <div
                key={contract.id}
                onClick={() => setSelectedContractId(contract.id)}
                className="group flex cursor-pointer flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-4 transition-all hover:border-primary/30 hover:shadow-[0_12px_32px_-4px_rgba(46,127,238,0.12)] sm:flex-row sm:items-center sm:gap-6"
              >
                {/* 좌: 상태 + 번호 + 프로젝트명 */}
                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 flex items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-[6px] border px-2 py-0.5 text-2xs font-semibold",
                        sc.bgColor,
                        sc.textColor,
                        sc.borderColor,
                      )}
                    >
                      <SIcon className="h-3 w-3" />
                      {sc.label}
                    </span>
                    <span className="text-2xs font-medium text-gray-400">
                      {contract.contractNumber}
                    </span>
                  </div>
                  <h3 className="truncate text-base font-semibold text-gray-900 group-hover:text-primary">
                    {contract.quoteRequest?.projectName || "프로젝트명 없음"}
                  </h3>
                </div>

                {/* 중: 발주자 / 금액 / 계약일 */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 sm:flex-nowrap">
                  <div className="flex items-center gap-1.5">
                    <Building className="h-4 w-4 text-primary/60" />
                    <span className="font-medium text-gray-700">
                      {contract.buyer?.name || "정보 없음"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Banknote className="h-4 w-4 text-primary/60" />
                    <span className="font-semibold text-primary">
                      {contract.totalAmount.toLocaleString("ko-KR")}원
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-primary/60" />
                    <span className="font-medium text-gray-700">
                      {new Date(contract.createdAt).toLocaleDateString("ko-KR")}
                    </span>
                  </div>
                </div>

                {/* 우: 액션 */}
                <div className="flex items-center gap-2 sm:border-l sm:border-gray-100 sm:pl-6">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasPdf) void handleDownloadPdf(contract);
                    }}
                    disabled={!hasPdf}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition-all",
                      hasPdf
                        ? "border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
                        : "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400",
                    )}
                  >
                    <Download className="h-4 w-4" />
                    {hasPdf ? "다운로드" : "생성 중"}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedContractId(contract.id);
                    }}
                    className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-all hover:border-primary/20 hover:bg-primary/5 hover:text-primary"
                  >
                    상세보기
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Contract Detail Modal with DI hook */}
      <Dialog
        open={!!selectedContractId}
        onOpenChange={(open) => !open && setSelectedContractId(null)}
      >
        <DialogContent
          size="2xl"
          className="p-0 overflow-hidden h-[90vh] sm:max-w-5xl z-[220] border-0 shadow-none"
          overlayClassName="z-[215]"
          hideClose
        >
          <DialogTitle className="sr-only">계약서 상세</DialogTitle>
          <DialogDescription className="sr-only">
            계약서의 상세 내용을 확인합니다.
          </DialogDescription>
          {selectedContractId && (
            <ContractSheetModal
              contractId={selectedContractId}
              onClose={() => setSelectedContractId(null)}
              useContractHook={useExternalContractDetail}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
